/**
 * Lightweight JSON Schema → form renderer.
 *
 * We intentionally support only the subset our connector + format descriptors
 * use today: string | integer | number | boolean | enum (string values) |
 * object (nested properties) | array of strings. Anything unknown renders as
 * a readonly JSON text input — good enough for the wizard without pulling
 * in a 200kB form library.
 */

import type { JsonSchema } from "~/lib/feeds";

interface SchemaFormProps {
    schema: JsonSchema;
    value: Record<string, unknown>;
    onChange: (next: Record<string, unknown>) => void;
    /** When true, any `required` props render with an aria-required input. */
    respectRequired?: boolean;
}

export function SchemaForm({
    schema,
    value,
    onChange,
    respectRequired = true,
}: SchemaFormProps): React.ReactElement {
    if (!schema || schema.type !== "object" || !schema.properties) {
        return (
            <div className="text-12 text-text-muted">
                No configuration required for this selection.
            </div>
        );
    }
    const req = new Set(schema.required ?? []);
    const keys = Object.keys(schema.properties);

    return (
        <div className="stack-sm">
            {keys.map((k) => {
                const s = schema.properties![k];
                const v = value[k];
                const isReq = respectRequired && req.has(k);
                return (
                    <FieldRow
                        key={k}
                        name={k}
                        schema={s}
                        value={v}
                        required={isReq}
                        onChange={(nv) => onChange({ ...value, [k]: nv })}
                    />
                );
            })}
        </div>
    );
}

function FieldRow({
    name,
    schema,
    value,
    required,
    onChange,
}: {
    name: string;
    schema: JsonSchema;
    value: unknown;
    required: boolean;
    onChange: (v: unknown) => void;
}): React.ReactElement {
    const label = (
        <span className="text-12 text-text-muted">
            {name}
            {required && <span className="text-danger" aria-hidden> *</span>}
            {schema.description ? (
                <span className="text-text-subtle" style={{ marginLeft: 6 }}>
                    {schema.description}
                </span>
            ) : null}
        </span>
    );

    // Enum → select
    if (schema.enum && schema.enum.length > 0) {
        return (
            <label className="stack-xs">
                {label}
                <select
                    className="input"
                    value={(value as string | number | undefined) ?? ""}
                    aria-required={required || undefined}
                    onChange={(e) => {
                        const raw = e.target.value;
                        // preserve numeric enums
                        const first = schema.enum![0];
                        onChange(typeof first === "number" ? Number(raw) : raw);
                    }}
                >
                    <option value="">— select —</option>
                    {schema.enum.map((opt) => (
                        <option key={String(opt)} value={String(opt)}>
                            {String(opt)}
                        </option>
                    ))}
                </select>
            </label>
        );
    }

    // Boolean → checkbox
    if (schema.type === "boolean") {
        return (
            <label className="cluster-xs">
                <input
                    type="checkbox"
                    checked={Boolean(value)}
                    onChange={(e) => onChange(e.target.checked)}
                />
                {label}
            </label>
        );
    }

    // Integer / number → number input
    if (schema.type === "integer" || schema.type === "number") {
        return (
            <label className="stack-xs">
                {label}
                <input
                    type="number"
                    className="input"
                    value={value == null ? "" : String(value)}
                    aria-required={required || undefined}
                    min={schema.minimum}
                    max={schema.maximum}
                    step={schema.type === "integer" ? 1 : "any"}
                    onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") {
                            onChange(undefined);
                            return;
                        }
                        const n = schema.type === "integer" ? parseInt(raw, 10) : parseFloat(raw);
                        onChange(Number.isNaN(n) ? undefined : n);
                    }}
                />
            </label>
        );
    }

    // Array of strings → comma-separated input
    if (schema.type === "array" && schema.items?.type === "string") {
        const arr = Array.isArray(value) ? (value as string[]) : [];
        return (
            <label className="stack-xs">
                {label}
                <input
                    className="input"
                    value={arr.join(", ")}
                    aria-required={required || undefined}
                    onChange={(e) => {
                        const parts = e.target.value
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean);
                        onChange(parts);
                    }}
                    placeholder="comma, separated, values"
                />
            </label>
        );
    }

    // Nested object — recurse. Keep one level; deeper stays as-is.
    if (schema.type === "object" && schema.properties) {
        return (
            <fieldset
                className="stack-xs"
                style={{
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md)",
                    padding: "var(--space-3)",
                }}
            >
                <legend className="text-12 text-text-muted">{name}</legend>
                <SchemaForm
                    schema={schema}
                    value={(value as Record<string, unknown>) ?? {}}
                    onChange={onChange as (n: Record<string, unknown>) => void}
                />
            </fieldset>
        );
    }

    // Default — string
    return (
        <label className="stack-xs">
            {label}
            <input
                className="input"
                value={(value as string | undefined) ?? ""}
                aria-required={required || undefined}
                onChange={(e) => onChange(e.target.value)}
                style={
                    name.toLowerCase().includes("url") || name.toLowerCase().includes("path")
                        ? { fontFamily: "var(--font-family-mono)" }
                        : undefined
                }
            />
        </label>
    );
}

/**
 * Walk a JSON Schema and produce a starter object with default values filled
 * in. Keeps the form from flashing empty-string errors on mount.
 */
export function schemaDefaults(schema: JsonSchema): Record<string, unknown> {
    if (!schema || schema.type !== "object" || !schema.properties) return {};
    const out: Record<string, unknown> = {};
    for (const [k, s] of Object.entries(schema.properties)) {
        if (s.default !== undefined) {
            out[k] = s.default;
        } else if (s.type === "object" && s.properties) {
            out[k] = schemaDefaults(s);
        } else if (s.type === "boolean") {
            out[k] = false;
        }
    }
    return out;
}
